import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiServer, FiGitBranch, FiCloud, FiShield, FiActivity,
  FiCheckCircle, FiChevronDown, FiChevronRight, FiCode,
  FiDatabase, FiLock, FiZap, FiLayers, FiBox, FiCpu,
  FiAlertTriangle, FiInfo, FiFileText, FiUsers, FiBook
} from 'react-icons/fi';
import { useSolution } from '../../../context/SolutionContext';
import { 
  DEVOPS_ARCHITECTURE, 
  ENGINEERING_STANDARDS, 
  GOVERNANCE_ARTIFACTS,
  REGULATORY_COMPLIANCE,
  RDT_CONTEXT,
  getAllDevOpsComponents 
} from '../../../data/devOpsArchitecture';

const DevOpsView = () => {
  const { state, dispatch } = useSolution();
  const [activeSection, setActiveSection] = useState('infrastructure');
  const [expandedItems, setExpandedItems] = useState(new Set());
  
  const toggleExpand = (id) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };
  
  const sections = [
    { id: 'infrastructure', name: 'IaC & Infrastructure', icon: FiServer },
    { id: 'cicd', name: 'CI/CD Pipelines', icon: FiGitBranch },
    { id: 'containers', name: 'Containers & Orchestration', icon: FiBox },
    { id: 'observability', name: 'Observability & SRE', icon: FiActivity },
    { id: 'durability', name: 'Durability & HA', icon: FiShield },
    { id: 'security', name: 'Secrets & Security', icon: FiLock },
    { id: 'events', name: 'Event-Driven', icon: FiZap },
    { id: 'serverless', name: 'Serverless', icon: FiCloud },
    { id: 'standards', name: 'Engineering Standards', icon: FiCode },
    { id: 'governance', name: 'Governance Artifacts', icon: FiFileText },
    { id: 'compliance', name: 'Regulatory Compliance', icon: FiInfo },
    { id: 'rdt', name: 'RDT Context', icon: FiUsers }
  ];
  
  const renderDevOpsCategory = (categoryKey) => {
    const category = DEVOPS_ARCHITECTURE[categoryKey];
    if (!category?.components) return null;
    
    return (
      <div className="space-y-4">
        {category.components.map(comp => {
          const isExpanded = expandedItems.has(comp.id);
          return (
            <div key={comp.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleExpand(comp.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{category.icon}</span>
                  <div className="text-left">
                    <div className="font-semibold text-gray-900">{comp.name}</div>
                    <div className="text-xs text-gray-500">{comp.purpose}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-green-600 font-semibold">
                    ${comp.cost.low} - ${comp.cost.high} {comp.cost.unit}
                  </span>
                  {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                </div>
              </button>
              
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="p-4 border-t border-gray-100 bg-gray-50 space-y-3">
                      <div>
                        <h4 className="text-xs font-bold text-gray-700 mb-1">Function</h4>
                        <p className="text-sm text-gray-600">{comp.function}</p>
                      </div>
                      
                      {comp.applications && (
                        <div>
                          <h4 className="text-xs font-bold text-gray-700 mb-1">Applications</h4>
                          <ul className="space-y-1">
                            {comp.applications.map((app, i) => (
                              <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                                <FiCheckCircle className="w-3 h-3 text-green-500 mt-1 flex-shrink-0" />
                                {app}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {comp.bestPractices && (
                        <div>
                          <h4 className="text-xs font-bold text-gray-700 mb-1">Best Practices</h4>
                          <ul className="space-y-1">
                            {comp.bestPractices.map((bp, i) => (
                              <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                                <FiCheckCircle className="w-3 h-3 text-blue-500 mt-1 flex-shrink-0" />
                                {bp}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {comp.alternatives && (
                        <div>
                          <h4 className="text-xs font-bold text-gray-700 mb-1">Alternatives</h4>
                          <div className="flex flex-wrap gap-1">
                            {comp.alternatives.map((alt, i) => (
                              <span key={i} className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                                {alt}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {comp.tags && (
                        <div className="flex flex-wrap gap-1">
                          {comp.tags.map((tag, i) => (
                            <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    );
  };
  
  const renderStandards = () => (
    <div className="space-y-6">
      {/* Programming Languages */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Programming Languages</h3>
        <div className="space-y-4">
          {ENGINEERING_STANDARDS.programmingLanguages.languages.map(lang => (
            <div key={lang.id} className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-900">{lang.name}</span>
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                  {lang.proficiency}
                </span>
              </div>
              <div className="space-y-2">
                <div>
                  <span className="text-xs font-bold text-gray-600">Use Cases:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {lang.useCases.map((uc, i) => (
                      <span key={i} className="text-xs text-gray-600">{uc}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* CI/CD Practices */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">CI/CD Best Practices</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {ENGINEERING_STANDARDS.ciCdPractices.practices.map((practice, i) => (
            <div key={i} className="p-3 bg-blue-50 rounded-lg">
              <div className="font-semibold text-gray-900 text-sm mb-1">{practice.name}</div>
              <p className="text-xs text-gray-600 mb-2">{practice.description}</p>
              <div className="flex flex-wrap gap-1">
                {practice.tools.map((tool, j) => (
                  <span key={j} className="px-2 py-0.5 bg-white text-gray-600 rounded text-xs">
                    {tool}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Code Quality */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Code Quality Standards</h3>
        <div className="space-y-3">
          {ENGINEERING_STANDARDS.codeQualityStandards.standards.map((std, i) => (
            <div key={i} className="p-3 bg-green-50 rounded-lg">
              <div className="font-semibold text-gray-900 text-sm mb-2">{std.name}</div>
              <ul className="space-y-1">
                {std.metrics.map((metric, j) => (
                  <li key={j} className="text-xs text-gray-600 flex items-center gap-2">
                    <FiCheckCircle className="w-3 h-3 text-green-500" />
                    {metric}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  
  const renderGovernance = () => (
    <div className="space-y-6">
      {Object.entries(GOVERNANCE_ARTIFACTS).map(([key, category]) => (
        <div key={key} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>{category.icon}</span>
            {category.category}
          </h3>
          
          {category.steps && (
            <div className="space-y-3">
              {category.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {step.step}
                  </span>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{step.name}</div>
                    {step.artifacts && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {step.artifacts.map((a, j) => (
                          <span key={j} className="px-2 py-0.5 bg-white text-gray-600 rounded text-xs">
                            {a}
                          </span>
                        ))}
                      </div>
                    )}
                    {step.duration && (
                      <p className="text-xs text-gray-500 mt-1">Duration: {step.duration}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {category.sections && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {category.sections.map((section, i) => (
                <div key={i} className="p-2 bg-blue-50 rounded text-xs text-gray-700">
                  {section}
                </div>
              ))}
            </div>
          )}
          
          {category.strategies && (
            <div className="space-y-3">
              {category.strategies.map((strategy, i) => (
                <div key={i} className="p-3 bg-purple-50 rounded-lg">
                  <div className="font-semibold text-gray-900 text-sm">{strategy.audience}</div>
                  <p className="text-xs text-gray-600 mt-1">Approach: {strategy.approach}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {strategy.artifacts.map((a, j) => (
                      <span key={j} className="px-2 py-0.5 bg-white text-gray-600 rounded text-xs">
                        {a}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
  
  const renderCompliance = () => (
    <div className="space-y-6">
      {Object.entries(REGULATORY_COMPLIANCE).map(([domainKey, domain]) => (
        <div key={domainKey} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">{domain.category}</h3>
          
          {domain.regulations && (
            <div className="space-y-4">
              {domain.regulations.map(reg => (
                <div key={reg.id} className="p-4 bg-gray-50 rounded-lg border-l-4 border-blue-500">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">{reg.name}</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                      {reg.scope}
                    </span>
                  </div>
                  {reg.requirements && (
                    <div className="mb-2">
                      <h4 className="text-xs font-bold text-gray-700 mb-1">Requirements:</h4>
                      <ul className="space-y-1">
                        {reg.requirements.map((req, i) => (
                          <li key={i} className="text-xs text-gray-600">• {req}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {reg.aiImplications && (
                    <div>
                      <h4 className="text-xs font-bold text-red-700 mb-1">AI Implications:</h4>
                      <ul className="space-y-1">
                        {reg.aiImplications.map((imp, i) => (
                          <li key={i} className="text-xs text-gray-600 flex items-start gap-1">
                            <FiAlertTriangle className="w-3 h-3 text-yellow-500 mt-0.5 flex-shrink-0" />
                            {imp}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {domain.frameworks && (
            <div className="space-y-3">
              {domain.frameworks.map(fw => (
                <div key={fw.id} className="p-4 bg-green-50 rounded-lg">
                  <div className="font-semibold text-gray-900">{fw.name}</div>
                  <p className="text-xs text-gray-600 mt-1">{fw.description}</p>
                  {fw.functions && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {fw.functions.map((f, i) => (
                        <span key={i} className="px-2 py-0.5 bg-white text-gray-600 rounded text-xs">
                          {f}
                        </span>
                      ))}
                    </div>
                  )}
                  {fw.riskLevels && (
                    <div className="mt-2 space-y-1">
                      {fw.riskLevels.map((rl, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs">
                          <span className={`px-2 py-0.5 rounded font-semibold ${
                            rl.level === 'Unacceptable' ? 'bg-red-100 text-red-700' :
                            rl.level === 'High' ? 'bg-orange-100 text-orange-700' :
                            rl.level === 'Limited' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {rl.level}
                          </span>
                          <span className="text-gray-600">{rl.examples?.join(', ')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
  
  const renderRDTContext = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">RDT Architectural Patterns</h3>
        <div className="space-y-3">
          {RDT_CONTEXT.architecturalPatterns.patterns.map((pattern, i) => (
            <div key={i} className="p-4 bg-gray-50 rounded-lg">
              <div className="font-semibold text-gray-900">{pattern.name}</div>
              <p className="text-sm text-gray-600 mt-1">{pattern.description}</p>
              <div className="flex flex-wrap gap-1 mt-2">
                {pattern.requirements.map((req, j) => (
                  <span key={j} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                    {req}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">RDT Business Functions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {RDT_CONTEXT.businessFunctions.functions.map((fn, i) => (
            <div key={i} className="p-3 bg-blue-50 rounded-lg text-sm text-gray-700 font-medium text-center">
              {fn}
            </div>
          ))}
        </div>
      </div>
      
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">AI Standards at RDT</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {RDT_CONTEXT.aiStandards.standards.map((std, i) => (
            <div key={i} className="p-3 bg-purple-50 rounded-lg text-sm text-gray-700 font-medium text-center">
              {std}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  
  return (
    <div className="h-full overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-3">
            <FiServer className="w-8 h-8 text-blue-600" />
            DevOps & Production Engineering
          </h2>
          <p className="text-gray-600">
            Complete DevOps architecture, SRE practices, and enterprise standards
          </p>
        </div>
        
        {/* Section Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${
                activeSection === section.id
                  ? 'bg-blue-500 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              <section.icon className="w-4 h-4" />
              {section.name}
            </button>
          ))}
        </div>
        
        {/* Section Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSection}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {activeSection === 'infrastructure' && renderDevOpsCategory('infrastructureAsCode')}
            {activeSection === 'cicd' && renderDevOpsCategory('cicdPipelines')}
            {activeSection === 'containers' && renderDevOpsCategory('containerOrchestration')}
            {activeSection === 'observability' && renderDevOpsCategory('observabilityAndSRE')}
            {activeSection === 'durability' && renderDevOpsCategory('durabilityAndHA')}
            {activeSection === 'security' && renderDevOpsCategory('secretsAndSecurity')}
            {activeSection === 'events' && renderDevOpsCategory('eventDrivenArchitecture')}
            {activeSection === 'serverless' && renderDevOpsCategory('serverlessComputing')}
            {activeSection === 'standards' && renderStandards()}
            {activeSection === 'governance' && renderGovernance()}
            {activeSection === 'compliance' && renderCompliance()}
            {activeSection === 'rdt' && renderRDTContext()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DevOpsView;